import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7c0oc8fgr5j5eoavgk0';

const FinalCTA = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const details = (e.currentTarget.elements.namedItem('project_details') as HTMLTextAreaElement)?.value || '';
    if (details.length > 500) return;
    setLoading(true);
    const data = new URLSearchParams();
    Array.from(new FormData(e.currentTarget)).forEach(([k, v]) => data.append(k, v as string));
    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="w-full overflow-hidden">
      {/* Top: real cinematic artwork headline */}
      <div className="relative py-36 px-8 md:px-16 lg:px-24 xl:px-32 flex flex-col items-center text-center overflow-hidden">
        <div className="absolute inset-0">
          {/* Real image from the site */}
          <img
            src="https://images.squarespace-cdn.com/content/v1/6205743531e70503b4cb51e0/1728142754373-88DARSDMGF9FCHPNL0OH/rabbit+and+hatter.jpg"
            alt="Storybook artwork by Deb's Art Designs"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#1A1612]/78"></div>
          <div className="absolute inset-0 grain-overlay"></div>
        </div>

        <div className="relative z-10 max-w-3xl">
          <div className="flex items-center justify-center gap-5 mb-14">
            <div className="w-16 h-px bg-[#C9A96E]/50"></div>
            <span className="text-[#C9A96E]/65 uppercase tracking-[0.5em] text-[9px] font-light">Commission a Piece</span>
            <div className="w-16 h-px bg-[#C9A96E]/50"></div>
          </div>

          <h2 className="font-serif text-white leading-[0.88] tracking-[-0.01em] mb-10" style={{ fontSize: 'clamp(3rem, 7vw, 6.5rem)', fontWeight: 300 }}>
            Own Something<br />
            That Doesn&apos;t Exist<br />
            <em className="italic text-[#D4B483]" style={{ fontWeight: 300 }}>Anywhere Else</em>
          </h2>

          <p className="text-white/40 text-sm leading-[2.2] tracking-[0.06em] font-light max-w-md mx-auto mb-16">
            Custom artwork designed specifically for your space and vision. Handcrafted, original, and made for you alone.
          </p>

          <a
            href="#contact-form"
            onClick={(e) => { e.preventDefault(); document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="whitespace-nowrap inline-block px-12 py-4 border border-[#C9A96E]/55 text-[#C9A96E]/80 text-[10px] font-light uppercase tracking-[0.5em] hover:bg-[#C9A96E]/15 hover:border-[#C9A96E]/80 transition-all duration-700 cursor-pointer"
          >
            Begin Your Artwork
          </a>
        </div>
      </div>

      {/* Form section */}
      <div id="contact-form" className="grid grid-cols-1 lg:grid-cols-2">
        {/* Left: contact info on warm dark */}
        <div className="bg-[#2C2420] px-10 md:px-16 lg:px-20 py-24 flex flex-col justify-center">
          <div className="max-w-sm">
            <h3 className="font-serif text-white/80 text-3xl mb-3" style={{ fontWeight: 300 }}>
              Start the Conversation
            </h3>
            <div className="w-10 h-px bg-[#C9A96E]/40 mb-8"></div>
            <p className="text-white/35 text-xs leading-[2.2] tracking-[0.05em] font-light mb-14">
              Tell us about your project and we&apos;ll get back to you within 1–2 business days. No commitment required — just a conversation.
            </p>

            <div className="flex flex-col gap-7">
              <div className="flex items-start gap-5">
                <div className="w-6 h-6 flex items-center justify-center text-[#C9A96E]/50 shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-xs"></i>
                </div>
                <div>
                  <p className="text-white/25 text-[10px] uppercase tracking-[0.4em] font-light mb-1">Studio</p>
                  <p className="text-white/50 text-xs tracking-wide font-light leading-relaxed">5246 Buttermilk Road<br />Pylesville, MD 21132</p>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="w-6 h-6 flex items-center justify-center text-[#C9A96E]/50 shrink-0 mt-0.5">
                  <i className="ri-instagram-line text-xs"></i>
                </div>
                <div>
                  <p className="text-white/25 text-[10px] uppercase tracking-[0.4em] font-light mb-1">Instagram</p>
                  <a href="https://www.instagram.com/debsartdesigns" target="_blank" rel="noopener noreferrer"
                    className="text-white/50 text-xs tracking-wide font-light hover:text-[#C9A96E]/70 transition-colors">
                    @debsartdesigns
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form on warm ivory */}
        <div className="bg-[#FAF7F3] px-10 md:px-16 lg:px-20 py-24 flex flex-col justify-center">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 flex items-center justify-center border border-[#C9A96E]/40 text-[#C9A96E]/70 mb-10">
                <i className="ri-check-line text-xl"></i>
              </div>
              <h3 className="font-serif text-[#1A1612]/70 text-3xl mb-4" style={{ fontWeight: 300 }}>Request Received</h3>
              <p className="text-[#1A1612]/40 text-xs max-w-xs leading-[2.1] tracking-wide font-light">
                Thank you for reaching out. We&apos;ll review your project and be in touch soon.
              </p>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-8">
              <div className="mb-2">
                <h3 className="font-serif text-[#1A1612]/75 text-2xl mb-3" style={{ fontWeight: 300 }}>
                  Request Custom Design
                </h3>
                <div className="w-8 h-px bg-[#C9A96E]/40"></div>
              </div>

              <div>
                <label className="block text-[9px] uppercase tracking-[0.45em] text-[#1A1612]/35 mb-3 font-light" htmlFor="name">
                  Full Name <span className="text-[#C9A96E]/70">*</span>
                </label>
                <input id="name" name="name" type="text" required placeholder="Your name"
                  className="w-full border-b border-[#1A1612]/12 bg-transparent pb-3 text-sm text-[#1A1612]/70 placeholder-[#1A1612]/25 focus:outline-none focus:border-[#C9A96E]/60 transition-colors font-light tracking-wide" />
              </div>

              <div>
                <label className="block text-[9px] uppercase tracking-[0.45em] text-[#1A1612]/35 mb-3 font-light" htmlFor="email">
                  Email Address <span className="text-[#C9A96E]/70">*</span>
                </label>
                <input id="email" name="email" type="email" required placeholder="your@email.com"
                  className="w-full border-b border-[#1A1612]/12 bg-transparent pb-3 text-sm text-[#1A1612]/70 placeholder-[#1A1612]/25 focus:outline-none focus:border-[#C9A96E]/60 transition-colors font-light tracking-wide" />
              </div>

              <div>
                <label className="block text-[9px] uppercase tracking-[0.45em] text-[#1A1612]/35 mb-3 font-light" htmlFor="phone">
                  Phone Number
                </label>
                <input id="phone" name="phone" type="tel" placeholder="(555) 000-0000"
                  className="w-full border-b border-[#1A1612]/12 bg-transparent pb-3 text-sm text-[#1A1612]/70 placeholder-[#1A1612]/25 focus:outline-none focus:border-[#C9A96E]/60 transition-colors font-light tracking-wide" />
              </div>

              <div>
                <label className="block text-[9px] uppercase tracking-[0.45em] text-[#1A1612]/35 mb-3 font-light" htmlFor="project_details">
                  Project Details <span className="text-[#C9A96E]/70">*</span>
                </label>
                <textarea id="project_details" name="project_details" required rows={4} maxLength={500}
                  placeholder="Describe the artwork you have in mind — style, size, colors, inspiration..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full border-b border-[#1A1612]/12 bg-transparent pb-3 text-sm text-[#1A1612]/70 placeholder-[#1A1612]/25 focus:outline-none focus:border-[#C9A96E]/60 transition-colors resize-none font-light tracking-wide" />
                <p className={`text-[9px] mt-1.5 text-right tracking-wide font-light ${charCount > 480 ? 'text-red-400/70' : 'text-[#1A1612]/20'}`}>{charCount}/500</p>
              </div>

              <button type="submit" disabled={loading || charCount > 500}
                className="whitespace-nowrap w-full py-5 bg-transparent border border-[#1A1612]/20 text-[#1A1612]/55 font-light text-[10px] uppercase tracking-[0.5em] hover:bg-[#2C2420] hover:text-white/80 hover:border-[#2C2420] transition-all duration-700 cursor-pointer disabled:opacity-30 mt-2">
                {loading ? 'Sending...' : 'Begin Your Project'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
